import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { acquireToken, fetchAdminPermissions } from './adminSlice';
import useSilentTokenRefresh from './utils/useSilentTokenRefresh';
import Loader from '../components/loader';
import Header from './components/header';
import Sidebar from './components/sidebar';
import OnboardingProfiles from './roles/hr/components/onboardingProfiles';
import GenerateLink from './roles/employee/components/generateLink';
import MyLinks from './roles/employee/components/myLinks';
import OnboardingSection from './components/onboarding';
import { UsersRound, Link2, ListChecks, Trophy } from 'lucide-react';
import {
  ADMIN_PREFIX,
  EMPLOYEE_PREFIX,
  GAMIFY_ADMIN_PREFIX,
  GAMIFY_EMPLOYEE_PREFIX,
  isEmployeeRole,
  isAdminRole,
  isBranchManagerRole,
  isPlayerOnlyRole,
  rolePrefix,
  gamifyPrefix,
  slugToPanel,
  PANEL_TO_URL,
  defaultPathForRole,
} from './utils/routeMap';

/**
 * AdminHome - Senior Level Architecture
 * Implements a fixed-shell layout with independent content scrolling.
 */
function AdminHome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, isAuthenticated, loading, role } = useSelector((state) => state.admin);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Derive active sidebar panel from URL slug (e.g. /admin/dashboard -> 'Onboarding')
  const slug = location.pathname.split('/').filter(Boolean)[1] || '';
  const panelFromUrl = slugToPanel(slug);
  const active = panelFromUrl?.sidebar || null;

  // Keeps the access token renewed in the background for the whole session.
  useSilentTokenRefresh();

  // Authentication & Permission Initialization
  useEffect(() => {
    if (!token) {
      dispatch(acquireToken())
        .unwrap()
        // No token (e.g. offline) → useSilentTokenRefresh retries and loads
        // permissions itself; firing them now would only 401.
        .then((result) => {
          if (result?.accessToken) dispatch(fetchAdminPermissions());
        })
    }
  }, [token, dispatch]);

  // Data is fetched lazily by each page on mount — no eager prefetch here.
  // OnboardingProfiles fetches /all-onboarding-profiles, MyLinks fetches
  // /access-token, etc. This keeps unrelated APIs from firing on login.

  // URL redirect — runs when path/role changes. Pure navigation, no fetching.
  // Employee paths: /employee/* or /gamify-employee/*. Admin paths: /admin/*
  // or /gamify-admin/*. Cross-role access redirects to the role's default page.
  useEffect(() => {
    if (!isAuthenticated || !role) return;

    const path = location.pathname;
    const isEmployeePath =
      path.startsWith(EMPLOYEE_PREFIX) || path.startsWith(GAMIFY_EMPLOYEE_PREFIX);
    const isAdminPath =
      path.startsWith(ADMIN_PREFIX) || path.startsWith(GAMIFY_ADMIN_PREFIX);

    const onRoot = !slug;
    const onWrongPrefix =
      (isEmployeeRole(role) && isAdminPath) ||
      (!isEmployeeRole(role) && isEmployeePath);
    if (onRoot || onWrongPrefix) {
      const dest = defaultPathForRole(role);
      if (dest) navigate(dest, { replace: true });
    }
  }, [isAuthenticated, role, slug, location.pathname, navigate]);

  // Sidebar click -> navigate (and close drawer on mobile).
  // 'Onboarding' uses the gamify prefix per role; everything else uses the
  // plain admin/employee prefix.
  const handleSidebar = (panelName) => {
    if (panelName === 'Onboarding') {
      navigate(`${gamifyPrefix(role)}/dashboard`);
    } else {
      const targetSlug = PANEL_TO_URL[panelName];
      if (targetSlug) navigate(`${rolePrefix(role)}/${targetSlug}`);
    }
    setSidebarOpen(false);
  };

  // Used by GenerateLink to switch to "My Links" tab (Employee only)
  const switchToMyLinks = () => navigate(`${EMPLOYEE_PREFIX}/my-links`);

  /**
   * Generates sidebar menu configuration based on user role
   */
  const getSidebarMenu = () => {
    // Onboarding section is shown to:
    //   - Super Admin / super admin → admin gamify view
    //   - Department users (IT, Finance, Maketing, …) → player gamify view
    // HR and Accounting do NOT see Onboarding — they only manage onboarding
    // profiles + links.
    // Branch managers (Employee / employees) do NOT see the gamify Onboarding
    // tab — they only get Generate Link / My Links.
    const onboardingMenuItem = {
      name: 'Onboarding',
      icon: <Trophy className="w-5 h-5" strokeWidth={2} />,
    };
    const canSeeOnboarding =
      role === 'Super Admin' || role === 'super admin' || isPlayerOnlyRole(role);
    const onboardingIfAllowed = canSeeOnboarding ? [onboardingMenuItem] : [];

    if (isAdminRole(role)) {
      return [
        {
          name: 'Onboarding Profiles',
          icon: <UsersRound className="w-5 h-5" strokeWidth={2} />
        },
        {
          name: 'Generate Link',
          icon: <Link2 className="w-5 h-5" strokeWidth={2} />
        },
        {
          name: 'My Links',
          icon: <ListChecks className="w-5 h-5" strokeWidth={2} />
        },
        ...onboardingIfAllowed, // Super Admin keeps Onboarding access.
      ];
    }

    if (isBranchManagerRole(role)) {
      return [
        {
          name: 'Generate Link',
          icon: <Link2 className="w-5 h-5" strokeWidth={2} />
        },
        {
          name: 'My Links',
          icon: <ListChecks className="w-5 h-5" strokeWidth={2} />
        },
        ...onboardingIfAllowed, // Empty for branch managers — no gamify Onboarding tab.
      ];
    }

    // Department users (IT / Finance / Maketing / Training / Branch Onboarding
    // / Human Resources) — only Onboarding is shown. Their dept-filtered
    // player view is the entire app for them.
    if (isPlayerOnlyRole(role)) {
      return onboardingIfAllowed;
    }

    // Role not loaded yet — return empty so we don't flash a stale "Onboarding-only" menu.
    if (!role) return [];

    return onboardingIfAllowed;
  }

  /**
   * Component router for main content area — driven by URL slug
   */
  const renderPanel = (name) => {
    const panels = {
      "Onboarding Profiles": <OnboardingProfiles />,
      "Generate Link": <GenerateLink switchToMyLinks={switchToMyLinks} />,
      "My Links": <MyLinks />,
      "Onboarding": <OnboardingSection />,
    };

    // Onboarding section renders its own padding (top tab bar is flush)
    if (name === "Onboarding") {
      return <div className="w-full animate-in fade-in duration-500">{panels[name]}</div>;
    }

    // Profiles owns its full-page background and responsive spacing.
    if (name === "Onboarding Profiles" || name === "Generate Link" || name === "My Links") {
      return <div className="flex w-full animate-in fade-in duration-500">{panels[name]}</div>;
    }

    return <div className="p-3 sm:p-6 w-full animate-in fade-in duration-500">{panels[name]}</div>;
  }

  const isFullWidthPage = active === "Onboarding Profiles" || active === "Generate Link" || active === "My Links";

  // The gamify Onboarding section paints its own cards on the shared #f2ece9
  // page background, so it drops the white panel the other centred pages use.
  const isCardlessPage = active === "Onboarding";

  return (
    <div className="flex h-screen overflow-hidden bg-white font-sans text-slate-900">
      { loading && <Loader />}

      {isAuthenticated && (
        <>
          {/* Fixed Sidebar */}
          <Sidebar
            active={active}
            sideBarMenu={getSidebarMenu()}
            handleSidebar={handleSidebar}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />

          {/* Right Shell */}
          <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

            {/* Fixed Header */}
            <Header onMenuClick={() => setSidebarOpen(true)} />

            {/* Independent Scrollable Content Area */}
            <main className="flex-1 overflow-y-auto bg-[#f2ece9] scroll-smooth pt-16">
              <div className={isFullWidthPage ? "min-h-full w-full" : isCardlessPage ? "max-w-[1600px] mx-auto w-full px-1 sm:px-3 lg:px-6 py-2 sm:py-4" : "max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-8 py-3 sm:py-6 lg:py-8"}>
                <div className={isFullWidthPage || isCardlessPage ? "flex min-h-[calc(100vh-4rem)] w-full overflow-hidden" : "bg-white shadow-sm border border-slate-200 rounded-xl min-h-[calc(100vh-160px)] flex overflow-hidden"}>
                  { role && renderPanel(active) }
                </div>
              </div>
            </main>
            
          </div>
        </>
      )}
    </div>
  );
}

export default AdminHome;
